// @need-translate
import {QueryClient} from '@tanstack/react-query';
import {fromPromise, UnknownActorLogic} from 'xstate';
import {ticketMutationAdapter} from '../../../../adapter/ticketMutationAdapter';
import {openCashCharge} from '../../../../cashcharge/openCashCharge.function';
import {DEFAULT_RENTAL_PERIOD_BY_MINUTE} from '../../../../common/constant';
import {getCurrentUrlWithAdditonalParam} from '../../../../common/getCurrentUrlWithAdditonalParam';
import {t} from '../../../../common/getTranslations';
import {ApiStatusCodeVo} from '../../../../common/shared/api/nicolas/vo/enums/ApiStatusCodeVo.enum';
import {TRIGGER_COMMAND_KEY} from '../../../../common/shared/constants';
import {
  COMMAND_KEY,
  saveCommand,
} from '../../../../common/shared/hooks/useCommandWithLocalStorage';
import {formatWithCommas} from '../../../../common/shared/utils/formatWithCommas';
import {SetListDialog} from '../../../../design-system/organisms/SetListDialog/SetListDialog';
import {SetBuySelectDialog} from '../../../../design-system/organisms/TicketDialog/SetBuySelectDialog';
import {TicketAskOwnTicketUseDialog} from '../../../../design-system/organisms/TicketDialog/TicketAskOwnTicketUseDialog';
import {TicketAskReopenCauseExpiredDialog} from '../../../../design-system/organisms/TicketDialog/TicketAskReopenCauseExpiredDialog';
import {TicketBuyAndUseDialog} from '../../../../design-system/organisms/TicketDialog/TicketBuyAndUseDialog';
import {TicketNonstopWatchingSelectTicketDialog} from '../../../../design-system/organisms/TicketDialog/TicketNonstopWatchingSelectTicketDialog';
import {TicketProductChoiceCauseExpiredDialog} from '../../../../design-system/organisms/TicketDialog/TicketProductChoiceCauseExpiredDialog';
import {
  removeSimpleDialog,
  showSimpleDialog,
  showStandaloneDialog,
} from '../../../../dialog/simpleDialogHandler';
import {commonErrorHandler} from '../../../../errors/commonErrorHandler';
import {getSerializedPagewebError} from '../../../../errors/getSerializedPagewebError.function';
import {PagewebError} from '../../../../errors/PagewebError.class';
import {PagewebErrorType} from '../../../../errors/PagewebErrorType.enum';
import {contentFactory} from '../../../../factory/contentFactory';
import {ticketFactory} from '../../../../factory/ticketFactory';
import {
  NormalListViewItem,
  QueryFromPage,
  ReadyToUseTicketQuery,
  SaleMethod,
  SetReadyToUseTicketQuery,
  SimpleSeriesSingleInfoQuery,
  TicketInfo,
  TicketKind,
  TicketSetBuyMutationPayload,
  TicketType,
} from '../../../../graphql/generated/types';
import {aggregateAndSendLog} from '../../../../logging/aggregateAndSendLog';
import {showToast} from '../../../../toast/toastHandler';
import {setNonstopWatchingSelectedBuyTicketInfo} from '../../../content/[seriesId]/viewer/[productId]/state/viewerInfoSlice';
import {generateRelayRedirectUrl} from '../../../relay/redirect/util/generateRelayRedirectUrl';
import {putUsedTicketUid} from '../state/ticketSlice';
import {BuyTicketFlowActors, BuyTicketFlowContext} from '../type';
import {getTicketKindByTicketType} from '../util/getTicketKindByTicketType';
import {getTicketPurchaseSuccessMessage} from '../util/getTicketName';
import {getTicketNameForLogging} from '../util/getTicketNameForLogging';
import {getTicketPopupOn, saveTicketPopupOn} from '../util/ticketPopupOn';

export const buyTicketFlowActors: {
  [x in keyof BuyTicketFlowActors]: UnknownActorLogic;
} = {
  showNonstopWatchingSelectTicket: fromPromise<
    | TicketInfo
    | {
        type: 'SET';
        isMultiple: boolean;
        setId?: string;
      },
    {context: BuyTicketFlowContext}
  >(async ({input}) => {
    return new Promise((resolve, reject) => {
      const context = input.context;
      if (!context.ticketProcessParam || !context.dispatch) {
        reject();
        return;
      }

      const dispatch = context.dispatch;
      const seriesId = context.ticketProcessParam.seriesId;
      const productId = context.ticketProcessParam.productId;
      const isMobile = !!context.isMobile;
      const readyToUseTicket = context.readyToUseTicket;

      showStandaloneDialog({
        dialogComponent: (
          <TicketNonstopWatchingSelectTicketDialog
            isMobile={isMobile}
            seriesId={seriesId}
            productId={productId}
            onClickCancel={reject}
            saleMethod={readyToUseTicket?.series?.saleMethod}
            availableTicketRentalType={
              readyToUseTicket?.available?.ticketRentalType
            }
            purchasableTicketRental={readyToUseTicket?.purchase?.ticketRental}
            availableTicketOwnType={readyToUseTicket?.available?.ticketOwnType}
            purchasableTicketOwn={readyToUseTicket?.purchase?.ticketOwn}
            onSelectTicket={ticketInfo => {
              dispatch(
                setNonstopWatchingSelectedBuyTicketInfo({
                  seriesId,
                  selectedBuyTicketInfo: ticketInfo,
                })
              );
              resolve(ticketInfo);
            }}
            set={context.readyToUseTicket?.purchase?.set}
            onClickSet={({setInfo}) => {
              resolve({
                type: 'SET',
                isMultiple: setInfo.isMultiple,
                ...(!setInfo.isMultiple && {setId: String(setInfo.setId)}),
              });
            }}
          />
        ),
      });
    });
  }),
  showAskOwnTicketUse: fromPromise<TicketType, {context: BuyTicketFlowContext}>(
    async ({input}) => {
      return new Promise((resolve, reject) => {
        const context = input.context;
        if (!context.ticketProcessParam) {
          reject();
          return;
        }

        const productId = context.ticketProcessParam.productId;
        const queryFrom = context.ticketProcessParam.queryFrom;
        const nonstopWatching = context.ticketProcessParam.nonstopWatching;
        const isMobile = !!context.isMobile;
        const readyToUseTicket = context.readyToUseTicket;
        const isShortsDrama = !!context.isShortsDrama;

        showStandaloneDialog({
          dialogComponent: (
            <TicketAskOwnTicketUseDialog
              isMobile={isMobile}
              productId={productId}
              ticketOwnCount={readyToUseTicket?.my?.ticketOwnCount || 0}
              ticketOwnType={readyToUseTicket?.available?.ticketOwnType}
              nonstopWatching={
                queryFrom === QueryFromPage.ViewerEntry ||
                queryFrom === QueryFromPage.Viewer
                  ? nonstopWatching
                  : undefined
              }
              onClickCancel={() =>
                reject(
                  new PagewebError({
                    isSerializedPagewebError: true,
                    type: PagewebErrorType.NeedToBuyTicketError,
                    message: '이용권 구매가 필요합니다.',
                  })
                )
              }
              onClickConfirm={() => {
                if (!readyToUseTicket?.available?.ticketOwnType) {
                  showToast({
                    message: '소장권이 없습니다',
                    isMobile,
                  });
                  reject(
                    new PagewebError({
                      isSerializedPagewebError: true,
                      type: PagewebErrorType.NeedToBuyTicketError,
                      message: '이용권 구매가 필요합니다.',
                    })
                  );
                  return;
                }
                resolve(readyToUseTicket.available.ticketOwnType);
              }}
              isShortsDrama={isShortsDrama}
            />
          ),
        });
      });
    }
  ),
  fetchSetBuy: fromPromise<
    TicketSetBuyMutationPayload,
    {
      context: BuyTicketFlowContext;
      ticketInfo: TicketInfo;
    }
  >(async ({input}) => {
    const context = input.context;
    if (
      !context.buySetProcessParam ||
      !context.queryClient ||
      !context.setBuyTicketInfo
    ) {
      throw new Error('fetchSetBuy param is required');
    }
    const queryClient = context.queryClient;
    const seriesId = context.buySetProcessParam.seriesId;
    const setId = context.buySetProcessParam.setId;
    const {ticketId} = context.setBuyTicketInfo;

    const options = ticketMutationAdapter.setBuyTicket();
    const variables = {
      input: {
        ticketId,
        setId,
        seriesId,
      },
    };
    const result = await queryClient
      .getMutationCache()
      .build(queryClient, options)
      .execute(variables);

    return result.setBuyTicket;
  }),
  buyAndUseTicket: fromPromise<
    void,
    {
      context: BuyTicketFlowContext;
      ticketInfo: TicketInfo;
      isCharged: boolean;
    }
  >(async ({input}) => {
    const context = input.context;
    if (
      !context.ticketProcessParam ||
      !context.queryClient ||
      !context.dispatch ||
      !context.buyAndUseTicketInfo
    ) {
      throw new Error('ticketProcessParam is required');
    }
    const queryClient = context.queryClient;
    const dispatch = context.dispatch;
    const seriesId = context.ticketProcessParam.seriesId;
    const productId = context.ticketProcessParam.productId;
    const isMobile = !!context.isMobile;
    const queryFrom = context.ticketProcessParam.queryFrom;
    const nonstopWatching = context.ticketProcessParam.nonstopWatching;
    const isShortsDrama = !!context.isShortsDrama;
    const {ticketId, ticketKind, price, discountPrice} =
      context.buyAndUseTicketInfo;

    const options = ticketMutationAdapter.buyAndUseTicket();
    const variables = {
      input: {
        ticketId,
        productId,
      },
    };
    const result = await queryClient
      .getMutationCache()
      .build(queryClient, options)
      .execute(variables);
    result?.buyAndUseTicket?.ticketUid &&
      dispatch(
        putUsedTicketUid({
          productId,
          ticketUid: result.buyAndUseTicket.ticketUid,
        })
      );
    const isCashType =
      context.readyToUseTicket?.series?.saleMethod === SaleMethod.Cash;
    const cashAmount = formatWithCommas(discountPrice ?? price);
    const message = isCashType
      ? ticketKind === TicketKind.Own
        ? t('purchase_cash_popup_successful_possession', {
            values: {value: cashAmount},
          })
        : t('purchase_cash_popup_successful_rent', {
            values: {value: cashAmount},
          })
      : ticketKind === TicketKind.Own
        ? t('purchase_ticket_popup_successful_possession', {
            values: {value: cashAmount},
          })
        : t('purchase_ticket_popup_successful_rent', {
            values: {value: cashAmount},
          });

    showToast({
      message,
      isMobile,
    });

    aggregateAndSendLog({
      actionType: 'Event',
      actionName: '구매_완료',
      actionKind: 'Purchase',
      eventMeta: {
        id: String(productId),
        type: 'productId',
      },
      click: {
        layer1: isCashType ? '캐시타입구매토스트' : '이용권구매토스트',
      },
      ecommerce: {
        price_amt: String(discountPrice ?? price),
        order_no: String(0),
      },
      customProps: {
        user_action: 'purchase',
        popup_type: isCashType ? '캐시타입구매토스트' : '이용권구매토스트',
        event_series_id: String(seriesId),
        event_episode_id: String(productId),
        event_ticket_type: `${
          ticketKind === TicketKind.Rent ? '대여' : '소장'
        }_${discountPrice ?? price}캐시`,
        event_ticket_cnt: String(1),
        ...(queryFrom === QueryFromPage.Viewer ||
        queryFrom === QueryFromPage.ViewerEntry
          ? {
              viewer_mode: nonstopWatching ? '정주행모드' : '일반모드',
            }
          : {}),
        is_charged: input.isCharged ? 'y' : 'n',
        ...(isShortsDrama && {
          is_shortdrama: isShortsDrama ? 'y' : undefined,
        }),
      },
    });
  }),
  useTicket: fromPromise<
    void,
    {
      context: BuyTicketFlowContext;
    }
  >(async ({input}) => {
    const context = input.context;
    if (
      !context.ticketProcessParam ||
      !context.queryClient ||
      !context.dispatch
    ) {
      throw new Error('ticketProcessParam is required');
    }

    const queryClient = context.queryClient;
    const dispatch = context.dispatch;
    const seriesId = context.ticketProcessParam.seriesId;
    const productId = context.ticketProcessParam.productId;
    const isMobile = !!context.isMobile;
    const ticketType =
      context.useTicketType ?? context.ticketProcessParam.ticketTypeToUseQuery;
    const isShortsDrama = !!context.isShortsDrama;

    const options = ticketMutationAdapter.useTicket();

    const variables = {
      input: {
        ...(ticketType && {ticketType}),
        productId,
      },
    };

    const fetchUseTicket = async () => {
      const result = await queryClient
        .getMutationCache()
        .build(queryClient, options)
        .execute(variables);
      result?.useTicket?.ticketUid &&
        dispatch(
          putUsedTicketUid({
            productId,
            ticketUid: result.useTicket.ticketUid,
          })
        );
    };

    try {
      await fetchUseTicket();
    } catch (error) {
      const pagewebError = getSerializedPagewebError(error);
      if (pagewebError.resultCode === ApiStatusCodeVo.WaitForPreviousRequest) {
        // -317 에러가 발생할 경우 2초 대기 후 API 재호출을 시도합니다. (앱과 스펙 통일)
        await new Promise(resolve => window.setTimeout(resolve, 2000));
        await fetchUseTicket();
      } else {
        throw error;
      }
    }

    // 세트 이용권은 사용 완료 토스트 노출 없음
    if (
      ticketType &&
      ![TicketType.RentSet, TicketType.BuySet].includes(ticketType)
    ) {
      showToast({
        message: getTicketPurchaseSuccessMessage(ticketType),
        isMobile,
      });
      aggregateAndSendLog({
        actionType: 'Event',
        actionName: '사용_완료',
        eventMeta: {
          id: String(productId),
          type: 'productId',
        },
        click: {
          layer1: '이용권사용토스트',
        },
        customProps: {
          user_action: 'use',
          popup_type: '이용권사용토스트',
          event_series_id: String(seriesId),
          event_episode_id: String(productId),
          event_ticket_type: `${
            getTicketKindByTicketType(ticketType) === TicketKind.Rent
              ? '대여'
              : '소장'
          }_${getTicketNameForLogging(ticketType)}`,
          event_ticket_cnt: String(1),
          ...(context.ticketProcessParam.queryFrom === QueryFromPage.Viewer ||
          context.ticketProcessParam.queryFrom === QueryFromPage.ViewerEntry
            ? {
                viewer_mode: context.ticketProcessParam.nonstopWatching
                  ? '정주행모드'
                  : '일반모드',
              }
            : {}),
          ...(isShortsDrama && {
            is_shortdrama: 'y',
          }),
        },
      });
    }
  }),
  fetchSeriesSingleInfo: fromPromise<
    SimpleSeriesSingleInfoQuery['simpleSeriesSingleInfo'],
    {
      queryClient: QueryClient;
      ticketProcessParam: BuyTicketFlowContext['ticketProcessParam'];
    }
  >(async ({input}) => {
    if (!input.ticketProcessParam) {
      throw new Error('ticketProcessParam is required');
    }

    try {
      const data = await input.queryClient.fetchQuery(
        contentFactory.simpleSeriesSingleInfo(
          {
            seriesId: input.ticketProcessParam.seriesId,
            productId: input.ticketProcessParam.productId,
          },
          {gcTime: 0, staleTime: 0}
        )
      );

      if (!data.simpleSeriesSingleInfo) {
        throw new PagewebError({
          isSerializedPagewebError: true,
          type: PagewebErrorType.ContentNotSaleError,
          message: '판매 중이 아닌 상품입니다.',
        });
      }

      return data.simpleSeriesSingleInfo;
    } catch (error) {
      const serialized = getSerializedPagewebError(error);
      if (serialized.resultCode === ApiStatusCodeVo.ItemNotSales) {
        throw new PagewebError({
          isSerializedPagewebError: true,
          type: PagewebErrorType.ContentNotSaleError,
          message: '판매 중이 아닌 상품입니다.',
        });
      } else {
        throw error;
      }
    }
  }),
  fetchReadyToUse: fromPromise<
    ReadyToUseTicketQuery['readyToUseTicket'],
    {
      context: BuyTicketFlowContext;
    }
  >(async ({input}) => {
    const {ticketProcessParam, queryClient} = input.context;
    if (!ticketProcessParam || !queryClient) {
      throw new Error('ticketProcessParam and queryClient is required');
    }
    const data = await queryClient.fetchQuery(
      ticketFactory.readyToUseTicket(
        {
          seriesId: ticketProcessParam.seriesId,
          productId: ticketProcessParam.productId,
        },
        {gcTime: 0, staleTime: 0}
      )
    );
    return data.readyToUseTicket;
  }),
  fetchSetReadyToUse: fromPromise<
    SetReadyToUseTicketQuery['setReadyToUseTicket'],
    {
      context: BuyTicketFlowContext;
    }
  >(async ({input}) => {
    const {buySetProcessParam, queryClient} = input.context;
    if (!buySetProcessParam || !queryClient) {
      throw new Error('buySetProcessParam and queryClient is required');
    }
    const data = await queryClient.fetchQuery(
      ticketFactory.setReadyToUseTicket(
        {
          seriesId: buySetProcessParam.seriesId,
          setId: buySetProcessParam.setId,
        },
        {gcTime: 0, staleTime: 0}
      )
    );
    return data.setReadyToUseTicket;
  }),
  showExpiredPopup: fromPromise<void, {context: BuyTicketFlowContext}>(
    async ({input}) => {
      return new Promise((resolve, reject) => {
        const context = input.context;
        if (!context.ticketProcessParam) {
          reject();
          return;
        }
        showStandaloneDialog({
          dialogComponent: (
            <TicketAskReopenCauseExpiredDialog
              isMobile={!!context.isMobile}
              productId={context.ticketProcessParam.productId}
              nonstopWatching={
                context.ticketProcessParam.queryFrom ===
                  QueryFromPage.ViewerEntry ||
                context.ticketProcessParam.queryFrom === QueryFromPage.Viewer
                  ? context.ticketProcessParam.nonstopWatching
                  : undefined
              }
              onClickCancel={() =>
                reject(
                  new PagewebError({
                    isSerializedPagewebError: true,
                    type: PagewebErrorType.NeedToBuyTicketError,
                    message: '이용권 구매가 필요합니다.',
                  })
                )
              }
              onClickConfirm={resolve}
              isShortsDrama={!!context.isShortsDrama}
            />
          ),
        });
        return;
      });
    }
  ),
  showProductChoiceCauseExpired: fromPromise<
    'NEXT' | 'CONTINUE',
    {context: BuyTicketFlowContext}
  >(async ({input}) => {
    return new Promise((resolve, reject) => {
      const context = input.context;
      if (!context.ticketProcessParam || !context.readyToUseTicket) {
        reject();
        return;
      }
      showStandaloneDialog({
        dialogComponent: (
          <TicketProductChoiceCauseExpiredDialog
            isMobile={!!context.isMobile}
            productId={context.ticketProcessParam.productId}
            onClickCancel={() =>
              reject(
                new PagewebError({
                  isSerializedPagewebError: true,
                  type: PagewebErrorType.NeedToBuyTicketError,
                  message: '이용권 구매가 필요합니다.',
                })
              )
            }
            onClickNext={() => {
              if (context.readyToUseTicket?.nextItem) {
                resolve('NEXT');
              } else {
                showSimpleDialog({
                  title: t('contenthome_last_ep_toast'),
                  isMobile: !!context.isMobile,
                  okButton: {
                    text: t('common_confirm_button'),
                    type: 'label',
                    onClick: reject,
                    closeAfterClick: true,
                  },
                  onClose: reject,
                });
              }
            }}
            onClickContinue={() => resolve('CONTINUE')}
          />
        ),
      });
      return;
    });
  }),
  showTicketChoicePopup: fromPromise<
    | {type: 'USE'; ticketType: TicketType}
    | {type: 'BUY_AND_USE'; ticketInfo: TicketInfo}
    | {
        type: 'SET';
        isMultiple: boolean;
        setId?: string;
      },
    {context: BuyTicketFlowContext}
  >(async ({input}) => {
    return new Promise((resolve, reject) => {
      const context = input.context;
      if (!context.ticketProcessParam || !context.readyToUseTicket) {
        reject('parameter is required');
        return;
      }

      showStandaloneDialog({
        dialogComponent: (
          <TicketBuyAndUseDialog
            isMobile={!!context.isMobile}
            productId={context.ticketProcessParam.productId}
            seriesId={context.ticketProcessParam.seriesId}
            saleMethod={context.readyToUseTicket.series?.saleMethod}
            nonstopWatching={
              context.ticketProcessParam.queryFrom ===
                QueryFromPage.ViewerEntry ||
              context.ticketProcessParam.queryFrom === QueryFromPage.Viewer
                ? context.ticketProcessParam.nonstopWatching
                : undefined
            }
            onClickCancel={() =>
              reject(
                new PagewebError({
                  isSerializedPagewebError: true,
                  type: PagewebErrorType.NeedToBuyTicketError,
                  message: '이용권 구매가 필요합니다.',
                  extra: {source: 'ticketChoicePopupCancel'},
                })
              )
            }
            triggerByPopupOn={!!context.ticketProcessParam.popupOn}
            localStoragePopupOn={getTicketPopupOn()}
            title={context.readyToUseTicket?.single?.title}
            waitfreeBlock={
              context.readyToUseTicket.series?.isWaitfree &&
              context.readyToUseTicket.single?.waitfreeBlock
            }
            ticketRentalCount={context.readyToUseTicket.my?.ticketRentalCount}
            ticketOwnCount={context.readyToUseTicket.my?.ticketOwnCount}
            cashAmount={context.readyToUseTicket.my?.cashAmount}
            availableTicketRentalType={
              context.readyToUseTicket.available?.ticketRentalType
            }
            purchasableTicketRental={
              context.readyToUseTicket.purchase?.ticketRental
            }
            availableTicketOwnType={
              context.readyToUseTicket.available?.ticketOwnType
            }
            purchasableTicketOwn={context.readyToUseTicket.purchase?.ticketOwn}
            rentalPeriodByMinute={
              context.readyToUseTicket.rentalPeriodByMinute ??
              DEFAULT_RENTAL_PERIOD_BY_MINUTE
            }
            onUseTicket={({ticketType, popupOff}) => {
              if (popupOff) {
                saveTicketPopupOn(false);
              }
              resolve({type: 'USE', ticketType});
            }}
            onBuyAndUseTicket={({ticketInfo, popupOff}) => {
              if (popupOff) {
                saveTicketPopupOn(false);
              }
              resolve({type: 'BUY_AND_USE', ticketInfo});
            }}
            set={context.readyToUseTicket.purchase?.set}
            onClickSet={({setInfo}) => {
              resolve({
                type: 'SET',
                isMultiple: setInfo.isMultiple,
                ...(!setInfo.isMultiple && {setId: String(setInfo.setId)}),
              });
            }}
            isShortsDrama={!!context.isShortsDrama}
          />
        ),
      });

      return;
    });
  }),
  showSetAllOwnedPopup: fromPromise<void, {context: BuyTicketFlowContext}>(
    async ({input}) => {
      return new Promise((resolve, reject) => {
        const context = input.context;
        if (!context.buySetProcessParam) {
          reject('param is required');
          return;
        }
        const {buySetProcessParam} = context;
        showSimpleDialog({
          isMobile: !!context.isMobile,
          title: t('api_content_set_fully_owned'),
          description: t(
            'sethome_set_purchase_completed_info_set_fully_owned',
            {
              values: {
                value: buySetProcessParam.seriesTitle,
              },
            }
          ),
          okButton: {
            text: t('sethome_set_button_purchased_info'),
            type: 'label',
            onClick: () => resolve(),
          },
          cancelButton: {
            text: t('common_confirm_button'),
            type: 'label',
            onClick: reject,
          },
          onClose: reject,
        });
      });
    }
  ),
  showSetAlreadyPurchasedPopup: fromPromise<
    void,
    {context: BuyTicketFlowContext}
  >(async ({input}) => {
    return new Promise((resolve, reject) => {
      const context = input.context;
      if (!context.buySetProcessParam) {
        reject('param is required');
        return;
      }
      const {buySetProcessParam} = context;
      showSimpleDialog({
        isMobile: !!context.isMobile,
        title: t('api_content_already_purchased'),
        description: t('sethome_set_purchase_completed_info', {
          values: {
            value: buySetProcessParam.seriesTitle,
          },
        }),
        okButton: {
          text: t('sethome_set_button_purchased_info'),
          type: 'label',
          onClick: () => resolve(),
        },
        cancelButton: {
          text: t('common_confirm_button'),
          type: 'label',
          onClick: reject,
        },
        onClose: reject,
      });
    });
  }),
  showSetBuyCompletePopup: fromPromise<
    'COMPLETE' | 'GO_TO_CONTENTHOME' | 'GO_TO_VIEWER',
    {context: BuyTicketFlowContext}
  >(async ({input}) => {
    return new Promise((resolve, reject) => {
      const context = input.context;
      if (!context.setBuyResult || !context.buySetProcessParam) {
        reject('context is required');
        return;
      }
      showSimpleDialog({
        isMobile: !!context.isMobile,
        title: t('sethome_set_purchase_completed'),
        description: t('sethome_set_purchase_complete_dialog_direct'),
        cancelButton: {
          text: t('common_confirm_button'),
          type: 'label',
          onClick: () => resolve('COMPLETE'),
        },
        okButton: {
          text: t('common_view_now_button'),
          type: 'label',
          onClick: () => {
            aggregateAndSendLog({
              actionType: 'Event',
              actionName: '바로보기_클릭',
              click: {
                layer1: '세트구매완료팝업',
              },
              customProps: {
                user_action: 'click',
                popup_type: '세트구매완료팝업',
                event_series_id: String(context.buySetProcessParam?.seriesId),
              },
            });
            resolve('GO_TO_VIEWER');
          },
          closeAfterClick: true,
        },
        onInView: () => {
          aggregateAndSendLog({
            actionType: 'Event',
            actionName: '구매_완료',
            actionKind: 'Purchase',
            click: {
              layer1: '세트구매완료팝업',
            },
            ecommerce: {
              price_amt: String(
                context.setBuyTicketInfo?.discountPrice ??
                  context.setBuyTicketInfo?.price
              ),
              order_no: String(0),
            },
            customProps: {
              user_action: 'purchase',
              popup_type: '세트구매완료팝업',
              event_series_id: String(context.buySetProcessParam?.seriesId),
              event_ticket_type: `${
                context.setBuyTicketInfo?.ticketKind === TicketKind.Rent
                  ? '대여'
                  : '소장'
              }_${
                context.setBuyTicketInfo?.discountPrice ??
                context.setBuyTicketInfo?.price
              }캐시`,
            },
          });
        },
      });
    });
  }),

  showSetBuySelectPopup: fromPromise<
    TicketInfo,
    {context: BuyTicketFlowContext}
  >(async ({input}) => {
    return new Promise((resolve, reject) => {
      const context = input.context;
      if (!context.buySetProcessParam || !context.setReadyToUseTicket) {
        reject('parameter is required');
        return;
      }
      showStandaloneDialog({
        dialogComponent: (
          <SetBuySelectDialog
            isMobile={!!context.isMobile}
            setId={context.buySetProcessParam.setId}
            seriesId={context.buySetProcessParam.seriesId}
            onClickCancel={reject}
            title={context.buySetProcessParam.setTitle}
            cashAmount={context.setReadyToUseTicket.my?.cashAmount}
            hasOwnProduct={!!context.setReadyToUseTicket.my?.hasOwnProduct}
            hasRentalProduct={
              !!context.setReadyToUseTicket.my?.hasRentalProduct
            }
            purchasableTicketRental={
              context.setReadyToUseTicket.purchase?.ticketRental
            }
            purchasableTicketOwn={
              context.setReadyToUseTicket.purchase?.ticketOwn
            }
            onBuySelect={ticketInfo => resolve(ticketInfo)}
          />
        ),
      });

      return;
    });
  }),
  showSetListDialog: fromPromise<
    {setId: string},
    {context: BuyTicketFlowContext}
  >(async ({input}) => {
    const queryClient = input.context.queryClient;

    if (!queryClient || !input.context.ticketProcessParam) {
      throw new Error('ticketProcessParam and queryClient is required');
    }

    const seriesId = input.context.ticketProcessParam.seriesId;
    const productId = input.context.ticketProcessParam.productId;

    const data = await queryClient.fetchQuery(
      contentFactory.contentSetList({
        seriesId,
        productId,
      })
    );

    const queryFrom = input.context.ticketProcessParam.queryFrom;
    const nonstopWatching = input.context.ticketProcessParam.nonstopWatching;

    return new Promise((resolve, reject) => {
      showStandaloneDialog({
        dialogComponent: (
          <SetListDialog
            isMobile={!!input.context.isMobile}
            setList={data.contentSetList?.setList as NormalListViewItem[]}
            onClickSet={(id: string) => {
              resolve({
                setId: id,
              });
            }}
            onClickCancel={reject}
            additionalTrackObj={
              queryFrom === QueryFromPage.ViewerEntry ||
              queryFrom === QueryFromPage.Viewer
                ? {
                    customProps: {
                      viewer_mode: nonstopWatching ? '정주행모드' : '일반모드',
                    },
                  }
                : undefined
            }
          />
        ),
      });
    });
  }),
  handleError: fromPromise<void, {context: BuyTicketFlowContext}>(
    async ({input}) => {
      // 뷰어 진입 시 발생하는 에러는 ViewerEntryErrorHandler 컴포넌트에서 대신 처리합니다.
      if (
        input.context.ticketProcessParam?.queryFrom ===
        QueryFromPage.ViewerEntry
      ) {
        removeSimpleDialog();
        return;
      }

      return new Promise(resolve => {
        if (!input.context.error) {
          resolve();
          return;
        }

        removeSimpleDialog();
        commonErrorHandler(input.context.error);
        resolve();
      });
    }
  ),
  showNeedLoginPopup: fromPromise<void, {context: BuyTicketFlowContext}>(
    async ({input}) => {
      return new Promise((resolve, reject) => {
        const context = input.context;
        showSimpleDialog({
          title: t('login_popup_for_access_content'),
          isMobile: !!context.isMobile,
          okButton: {
            text: t('login_popup_signin'),
            type: 'label',
            onClick: () => {
              resolve();
            },
          },
          cancelButton: {
            text: t('common_cancel_button'),
            type: 'label',
            onClick: reject,
          },
          onClose: reject,
        });
      });
    }
  ),
  openCashCharge: fromPromise<void, {context: BuyTicketFlowContext}>(
    async ({input}) => {
      return new Promise((resolve, reject) => {
        const context = input.context;

        let chargeAmount = 0;
        let event_series_id, event_episode_id, cash_cvr, commandKey;

        if (context.flowType === 'TICKET_PROCESS') {
          const ticketInfo =
            context.buyAndUseTicketInfo ??
            context.selectedNonstopWatchingBuyTicketInfo;
          const processParam = context.ticketProcessParam;

          if (!ticketInfo || !processParam) {
            reject();
            return;
          }

          chargeAmount =
            (ticketInfo.discountPrice || ticketInfo.price || 0) -
            (context.readyToUseTicket?.my?.cashAmount || 0);
          event_series_id = String(processParam.seriesId);
          event_episode_id = String(processParam.productId);
          cash_cvr =
            processParam.queryFrom === QueryFromPage.ContentHome
              ? '콘텐츠홈'
              : processParam.queryFrom === QueryFromPage.SetOverview
                ? '세트상품'
                : '뷰어';
          commandKey = COMMAND_KEY.CLICK_BUY_TICKETS;

          saveCommand({
            commandKey,
            command: {
              ...processParam,
              location: window.location.pathname,
              expiredConfirmed: context.expiredConfirmed,
              storedTicketInfo: ticketInfo,
              currentProductId: context.currentProductId,
            },
          });
        } else {
          const ticketInfo = context.setBuyTicketInfo;
          const processParam = context.buySetProcessParam;

          if (!ticketInfo || !processParam) {
            reject();
            return;
          }

          chargeAmount =
            (ticketInfo.discountPrice || ticketInfo.price || 0) -
            (context.setReadyToUseTicket?.my?.cashAmount || 0);
          event_series_id = String(processParam.seriesId);
          event_episode_id = String(processParam.setId);
          cash_cvr =
            processParam.queryFrom === QueryFromPage.ContentHome
              ? '콘텐츠홈'
              : processParam.queryFrom === QueryFromPage.SetOverview
                ? '세트상품'
                : '뷰어';
          commandKey = COMMAND_KEY.CLICK_SET_BUY;

          saveCommand({
            commandKey,
            command: {
              ...processParam,
              location: window.location.pathname,
              storedTicketInfo: ticketInfo,
            },
          });
        }

        openCashCharge({
          returnUrl: generateRelayRedirectUrl(
            getCurrentUrlWithAdditonalParam(TRIGGER_COMMAND_KEY, commandKey)
          ),
          chargeAmount,
          cashChargeInfo: {
            event_series_id,
            event_episode_id,
            cash_cvr,
          },
        });

        resolve();
      });
    }
  ),
};
